import {
  EnvironmentInjector,
  inject,
  Injectable,
  runInInjectionContext,
} from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, takeWhile } from 'rxjs/operators';
import { from, Observable, of, Subscriber } from 'rxjs';
import { StaticActions } from './static.actions';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  DocumentData,
  onSnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import {
  PermanentTag,
  StaticPermanentTagsContainer,
} from '@enrollhere/classes';
import { Action } from '@ngrx/store';
import { httpsCallable } from 'firebase/functions';
import { AuthService } from '../../../services/auth.service';
import { FIRESTORE, FUNCTIONS } from 'src/app/common/firebase/firebase.tokens';

/**
 * @deprecated NgRx effects with direct Firestore access are deprecated. Use API endpoints instead.
 */
@Injectable()
export class StaticEffects {
  private actions$ = inject(Actions);
  private authService = inject(AuthService);
  private environmentInjector = inject(EnvironmentInjector);
  /** @deprecated Direct Firestore access is deprecated. Use API endpoints instead. */
  private firestore = inject(FIRESTORE);
  /** @deprecated Direct Firebase Functions access is deprecated. Use API endpoints instead. */
  private functions = inject(FUNCTIONS);

  private staticTagsSubscription: { [key: string]: Unsubscribe } = {};

  private getStaticTagsDocRef(agencyId: string) {
    return doc(this.firestore, `agencies/${agencyId}/static/permanentTags`);
  }

  private getStaticTagsDoc(docRef: ReturnType<typeof doc>) {
    return getDoc(docRef);
  }

  private mapVisibleTags(
    byId: Record<string, unknown>,
    permissions: { hasTags: boolean; allTags: string[] }
  ): PermanentTag[] {
    const tags: PermanentTag[] = [];
    for (const [id, tag] of Object.entries(byId || {})) {
      if (!permissions?.hasTags || permissions.allTags.includes(id)) {
        tags.push(
          PermanentTag.fromJSON({
            ...(tag as object),
            id,
          })
        );
      }
    }
    return tags;
  }

  private emitLoadStaticTagsSuccess(
    agencyId: string,
    data: unknown,
    subscriber: Subscriber<Action>
  ) {
    if (data && (data as Record<string, unknown>)['byId']) {
      this.authService.permissionsConsolidated$
        .pipe(takeWhile(p => !p?.p, true))
        .subscribe(p => {
          if (p.p) {
            const tags = this.mapVisibleTags(
              ((data as Record<string, unknown>)['byId'] || {}) as Record<
                string,
                unknown
              >,
              {
                hasTags: !!p.hasTags,
                allTags: p.allTags || [],
              }
            );

            console.log('[static-effects] tags', tags);
            subscriber.next(
              StaticActions.loadStaticTagsSuccess({
                agencyId,
                tags,
              })
            );
          }
        });
    }
  }

  private emitLoadStaticTagsFailure(
    subscriber: Subscriber<Action>,
    error: unknown
  ) {
    console.error('[static-effects] error', error);
    subscriber.next(
      StaticActions.loadStaticTagsFailure({
        error: this.normalizeStaticTagError(error),
      })
    );
  }

  private normalizeStaticTagError(error: unknown): Error | null {
    if (error instanceof Error) {
      return error;
    }
    if (error === null || error === undefined) {
      return null;
    }
    return new Error(String(error));
  }

  private createUpdateStaticTagSuccess(agencyId: string, tag: PermanentTag) {
    return StaticActions.updateStaticTagSuccess({
      agencyId,
      tag,
    });
  }

  private createAddStaticTagPayload(tag: PermanentTag, id: string) {
    return {
      ...(PermanentTag.toJSON(tag) as object),
      id,
    };
  }

  private createAddStaticTagSuccess(agencyId: string, tag: PermanentTag) {
    return StaticActions.addStaticTagSuccess({
      agencyId,
      tag,
    });
  }

  loadStaticTags$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(StaticActions.loadStaticTags),
      mergeMap(({ agencyId }) =>
        new Observable<Action>(subscriber => {
          if (this.staticTagsSubscription[agencyId]) {
            subscriber.complete();
            return;
          }
          runInInjectionContext(this.environmentInjector, () => {
            this.staticTagsSubscription[agencyId] = onSnapshot(
              doc(this.firestore, `agencies/${agencyId}/static/permanentTags`),
              doc => {
                this.emitLoadStaticTagsSuccess(
                  agencyId,
                  doc.data(),
                  subscriber
                );
              },
              error => {
                this.emitLoadStaticTagsFailure(subscriber, error);
              }
            );
          });
          return () => this.staticTagsSubscription[agencyId]();
        }).pipe(
          catchError(error =>
            of(
              StaticActions.loadStaticTagsFailure({
                error: this.normalizeStaticTagError(error),
              })
            )
          )
        )
      )
    );
  });

  updateStaticTag$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(StaticActions.updateStaticTag),
      mergeMap(async ({ agencyId, tag }) => {
        try {
          const docRef = this.getStaticTagsDocRef(agencyId);
          await updateDoc(docRef, {
            [`byId.${tag.id}`]: PermanentTag.toJSON(tag),
          } as DocumentData);
          return this.createUpdateStaticTagSuccess(agencyId, tag);
        } catch (error) {
          return StaticActions.updateStaticTagFailure({
            error: error as Error,
          });
        }
      })
    );
  });

  addStaticTag$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(StaticActions.addStaticTag),
      mergeMap(async ({ agencyId, tag }) => {
        try {
          const docRef = this.getStaticTagsDocRef(agencyId);

          const id = await this.generateTagId(agencyId);

          const newTag = this.createAddStaticTagPayload(
            tag,
            id
          ) as PermanentTag;

          await setDoc(
            docRef,
            {
              byId: { [newTag.id]: newTag },
            },
            { merge: true }
          );
          return this.createAddStaticTagSuccess(agencyId, newTag);
        } catch (error) {
          return StaticActions.addStaticTagFailure({
            error: error as Error,
          });
        }
      })
    );
  });

  async generateTagId(agencyId: string) {
    const chars =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const generateId = (agencyId: string) => {
      let result = '';
      for (let i = 0; i < 4; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return `${agencyId}#-#${result}`;
    };

    const docRef = this.getStaticTagsDocRef(agencyId);
    const docSnap = await this.getStaticTagsDoc(docRef);

    if (!docSnap.exists()) {
      return generateId(agencyId);
    }

    const data = StaticPermanentTagsContainer.fromJSON(docSnap.data());
    const existingTags = data?.byId || {};

    let newId = generateId(agencyId);
    let count = 0;
    while (existingTags[newId]) {
      newId = generateId(agencyId);
      count++;
      if (count > 100) {
        throw new Error('Failed to generate a unique tag ID');
      }
    }

    return newId;
  }

  loadStaticTeams$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(StaticActions.loadStaticTeams),
      mergeMap(({ agencyId }) => {
        const teamsGet = httpsCallable(this.functions, 'teamsGet');

        return from(teamsGet({ agencyId })).pipe(
          map((result: any) => {
            const data = result.data;

            return StaticActions.loadStaticTeamsSuccess({
              agencyId,
              teams: data.hierarchy || { byNodeId: {}, rootNodeIds: [] },
              showLevels: data.showLevels,
              hasFullAccess: data.hasFullAccess,
            });
          }),
          catchError(error => {
            console.error('Error loading teams via Firebase function', error);
            return of(StaticActions.loadStaticTeamsFailure({ error }));
          })
        );
      })
    );
  });
}
